import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Maximize2, Banknote, Building, ShieldCheck, Heart, MessageSquare, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import ShareButton from "@/components/ShareButton";
import ServiceConversionSidebar from "@/components/ServiceConversionSidebar";
import { api, fmtFJD, fileUrl, formatApiError } from "@/lib/api";

export default function PropertyDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [favorited, setFavorited] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);
    const [msg, setMsg] = useState("");
    const [sending, setSending] = useState(false);

    useEffect(() => {
        api.get(`/listings/${id}`).then(({ data }) => setListing(data)).catch(() => {}).finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div className="max-w-7xl mx-auto px-4 py-20 font-app text-muted-foreground">Loading…</div>;
    if (!listing) return <div className="max-w-7xl mx-auto px-4 py-20"><h1 className="font-display text-2xl">Listing not found</h1></div>;

    const photos = (listing.photos?.map(fileUrl) || []).concat(listing.external_photos || []);
    if (photos.length === 0) photos.push("https://images.unsplash.com/photo-1564639918288-feb760ab20a4?crop=entropy&cs=srgb&fm=jpg&q=85");

    const toggleFav = async () => {
        if (!user) return navigate("/login");
        try {
            const { data } = await api.post("/favorites/toggle", { listing_id: id });
            setFavorited(data.favorited);
            toast.success(data.favorited ? "Saved to favorites" : "Removed from favorites");
        } catch (e) { toast.error("Could not update favorites"); }
    };

    const sendMessage = async () => {
        if (!user) return navigate("/login");
        if (!msg.trim()) return;
        setSending(true);
        try {
            await api.post("/messages", { listing_id: id, content: msg });
            toast.success("Message sent — routed via Bula Real Estate Fiji.");
            setContactOpen(false);
            setMsg("");
            navigate("/messages");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setSending(false); }
    };

    return (
        <div data-testid="property-detail-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                <Button variant="ghost" onClick={() => navigate(-1)} className="font-app">
                    <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </Button>
            </div>

            {/* Photo grid hero */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 rounded-xl overflow-hidden">
                    <div className="md:col-span-2 md:row-span-2 aspect-[16/10] md:aspect-auto">
                        <img src={photos[0]} alt="hero" className="w-full h-full object-cover" />
                    </div>
                    {photos.slice(1, 5).map((p, i) => (
                        <img key={i} src={p} alt="gallery" className="w-full aspect-[4/3] object-cover" />
                    ))}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 grid lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2">
                    <div className="flex items-center gap-2 mb-3">
                        <Badge className="bg-ocean text-white">{listing.location_island}</Badge>
                        {listing.listing_type !== "sale" && <Badge className="bg-coral text-white">Joint Venture Available</Badge>}
                        <Badge className="bg-fiji-green/15 text-fiji-green border-0"><ShieldCheck className="h-3 w-3 mr-1" /> Verified</Badge>
                    </div>
                    <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
                        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight flex-1">{listing.title}</h1>
                        <ShareButton type="listing" id={listing.id} title={listing.title} />
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground font-app mb-6">
                        <MapPin className="h-4 w-4 text-coral" /> {listing.location_region}, {listing.location_island}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                        <Card className="p-4">
                            <Maximize2 className="h-4 w-4 text-coral mb-1" />
                            <div className="text-xs text-muted-foreground font-app">Land size</div>
                            <div className="font-display text-xl font-bold">{listing.land_size_acres} ac</div>
                        </Card>
                        <Card className="p-4">
                            <Building className="h-4 w-4 text-fiji-green mb-1" />
                            <div className="text-xs text-muted-foreground font-app">Zoning</div>
                            <div className="font-display text-base font-bold">{listing.zoning}</div>
                        </Card>
                        <Card className="p-4">
                            <Banknote className="h-4 w-4 text-coral mb-1" />
                            <div className="text-xs text-muted-foreground font-app">Asking price</div>
                            <div className="font-display text-base font-bold">{fmtFJD(listing.price_fjd)}</div>
                        </Card>
                        <Card className="p-4">
                            <Calendar className="h-4 w-4 text-turquoise mb-1" />
                            <div className="text-xs text-muted-foreground font-app">Ownership</div>
                            <div className="font-display text-base font-bold capitalize">{listing.ownership_type?.replace("_", " ")}</div>
                        </Card>
                    </div>

                    <div className="prose prose-slate dark:prose-invert max-w-none font-app">
                        <h2 className="font-display text-2xl font-bold mb-3">About this land</h2>
                        <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{listing.description}</p>
                    </div>

                    <div className="mt-8 bg-sand/40 dark:bg-secondary/50 border-l-4 border-coral rounded-md p-6">
                        <h3 className="font-display text-xl font-bold mb-2">Development Potential</h3>
                        <p className="text-muted-foreground font-app">{listing.development_potential}</p>
                    </div>
                </div>

                <aside className="lg:col-span-1 space-y-4">
                    <Card className="p-6 sticky top-24">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-app mb-1">Asking price</div>
                        <div className="font-display text-3xl font-bold text-ocean dark:text-turquoise mb-4">{fmtFJD(listing.price_fjd)}</div>

                        <div className="bg-coral/10 border border-coral/30 rounded-md p-3 mb-4">
                            <div className="flex items-start gap-2">
                                <ShieldCheck className="h-4 w-4 text-coral flex-shrink-0 mt-0.5" />
                                <p className="text-xs font-app text-muted-foreground">
                                    All conversations and offers are routed through Bula Real Estate Fiji as the official intermediary. Direct buyer-seller contact is not allowed.
                                </p>
                            </div>
                        </div>

                        <Button onClick={() => setContactOpen(true)} data-testid="contact-platform-btn" className="w-full bg-ocean-green text-white hover:opacity-90 font-app mb-2">
                            <MessageSquare className="h-4 w-4 mr-2" /> Inquire via Platform
                        </Button>
                        <Button variant="outline" onClick={toggleFav} data-testid="favorite-btn" className="w-full font-app">
                            <Heart className={`h-4 w-4 mr-2 ${favorited ? "fill-coral text-coral" : ""}`} /> {favorited ? "Saved" : "Save listing"}
                        </Button>

                        {(listing.listing_type === "jv" || listing.listing_type === "both") && (
                            <Button asChild variant="ghost" className="w-full mt-2 text-coral font-app">
                                <Link to="/jv">View Joint Venture opportunities</Link>
                            </Button>
                        )}
                    </Card>

                    <ServiceConversionSidebar
                        context={{
                            kind: "property",
                            priceFjd: listing.price_fjd,
                            ownershipType: listing.ownership_type,
                            zoning: listing.zoning,
                            locationIsland: listing.location_island,
                            propertyKind: listing.property_kind,
                        }}
                        listingTitle={listing.title}
                        listingUrl={typeof window !== "undefined" ? window.location.href : ""}
                    />
                </aside>
            </section>

            <Dialog open={contactOpen} onOpenChange={setContactOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">Contact via Bula Real Estate Fiji</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground font-app">
                        Your message is securely routed through our platform. Our team facilitates the conversation, document exchange, and any offer process.
                    </p>
                    <Textarea data-testid="inquiry-textarea" value={msg} onChange={(e) => setMsg(e.target.value)} rows={5} placeholder="Hi, I'm interested in this property..." />
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setContactOpen(false)}>Cancel</Button>
                        <Button onClick={sendMessage} disabled={sending} data-testid="send-inquiry-btn" className="bg-coral text-white hover:bg-coral-deep">
                            {sending ? "Sending…" : "Send via platform"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
